import {
  doc,
  getDoc,
  setDoc,
  onSnapshot,
  serverTimestamp,
} from 'firebase/firestore';
import { db } from '../firebase';
import { Portfolio, PortfolioHolding } from '../types';
import { TransactionService } from './transactionService';
import { HoldingsCalculationService } from './holdingsCalculationService';
import { logger } from '../utils/logger';

/**
 * 投資組合服務
 * 只儲存持股數量和成本，價格在前端即時計算
 *
 * portfolios 文件以 accountId 為文件 ID,create 時由 rules 驗證帳號成員身分;
 * allowedUsers 由帳號文件同步(SCHEMA-01)。
 */
class _PortfolioService {
  private static readonly COLLECTION_NAME = 'portfolios';

  /**
   * 取得帳戶的投資組合（只有基本持股資訊）
   */
  static async getUserPortfolio(accountId: string): Promise<Portfolio | null> {
    try {
      const portfolioRef = doc(db, this.COLLECTION_NAME, accountId);
      const portfolioDoc = await getDoc(portfolioRef);

      if (portfolioDoc.exists()) {
        const data = portfolioDoc.data();
        return {
          accountId: data.accountId ?? accountId,
          holdings: data.holdings || [],
        };
      }

      // Create empty portfolio if doesn't exist
      await this.createEmptyPortfolio(accountId);
      return {
        accountId,
        holdings: [],
      };
    } catch (error) {
      logger.portfolio.error('Failed to get user portfolio', error);
      return null;
    }
  }

  /**
   * 為新帳戶創建空的投資組合(allowedUsers 由帳號文件同步)
   */
  static async createEmptyPortfolio(accountId: string): Promise<void> {
    try {
      const portfolioRef = doc(db, this.COLLECTION_NAME, accountId);

      // 讀取帳戶文件取得 allowedUsers(rules create 需驗證成員身分)
      const accountSnap = await getDoc(doc(db, 'tradingAccounts', accountId));
      const allowedUsers: string[] = accountSnap.exists()
        ? (accountSnap.data().allowedUsers ?? [])
        : [];

      await setDoc(portfolioRef, {
        accountId,
        allowedUsers,
        holdings: [],
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
      });
    } catch (error) {
      logger.portfolio.error('Failed to create empty portfolio', error);
      throw new Error('Failed to create portfolio');
    }
  }

  /**
   * 從交易記錄重新計算投資組合（只儲存基本資訊）
   */
  static async recalculatePortfolio(accountId: string): Promise<Portfolio | null> {
    try {
      // Get all account transactions
      const transactions = await TransactionService.getUserTransactions(accountId);

      if (transactions.length === 0) {
        return {
          accountId,
          holdings: [],
        };
      }

      // 使用統一的持股計算服務
      const { currentHoldings } = HoldingsCalculationService.calculateHoldingsFromTransactions(
        transactions,
        [], // 此處不需要股價資料
        { includeFees: true, includeHistorical: false }
      );

      // 轉換為 Portfolio 格式（只保留基本資訊）
      const holdings: PortfolioHolding[] = currentHoldings.map(holding => ({
        symbol: holding.symbol,
        quantity: holding.currentQuantity,
        avgCost: holding.avgCost,
      }));

      const portfolio: Portfolio = {
        accountId,
        holdings,
      };

      // Update portfolio in Firestore
      await this.updatePortfolio(accountId, portfolio);

      return portfolio;
    } catch (error) {
      logger.portfolio.error('Failed to recalculate portfolio', error);
      return null;
    }
  }

  /**
   * 更新投資組合到 Firestore(merge:不得覆蓋 allowedUsers 等 rules 管理欄位)
   */
  static async updatePortfolio(accountId: string, portfolio: Portfolio): Promise<void> {
    try {
      const portfolioRef = doc(db, this.COLLECTION_NAME, accountId);
      await setDoc(portfolioRef, {
        accountId,
        holdings: portfolio.holdings,
        updatedAt: serverTimestamp(),
      }, { merge: true });
    } catch (error) {
      logger.portfolio.error('Failed to update portfolio', error);
      throw new Error('Failed to update portfolio');
    }
  }

  /**
   * 即時監聽投資組合變更（只有基本資訊）
   */
  static subscribeToPortfolio(
    accountId: string,  // 交易帳戶 ID
    callback: (portfolio: Portfolio | null) => void
  ): () => void {
    const portfolioRef = doc(db, this.COLLECTION_NAME, accountId);

    return onSnapshot(portfolioRef, (doc) => {
      if (doc.exists()) {
        const data = doc.data();
        callback({
          accountId: data.accountId ?? accountId,
          holdings: data.holdings || [],
        });
      } else {
        callback(null);
      }
    }, (error) => {
      // 監聽錯誤保留最後已知狀態,不回呼 null(避免 UI 誤判為無資料)
      logger.portfolio.error('Portfolio subscription error', error);
    });
  }

}

// 導出服務
export class PortfolioService extends _PortfolioService {
  // 所有方法都繼承自 _PortfolioService
}
